import React, { useEffect, useRef, useState } from 'react';
import { IconCheck, IconClose } from 'lib/icons';
import { INPUT_SIZES } from 'lib/input';
import { TruncationTitleLogic } from 'lib/truncation';
import { useOutsideClickEventListener } from 'lib/utilities';
import PropTypes from 'prop-types';
import {
  StyledTextContainer,
  StyledEditIcon,
  StyledEditIconWrapper,
  StyledErrorMessage,
  StyledInputContainer,
  StyledInlineInput,
  StyledIndeterminateProgressCircle,
  StyledButtonContainer,
  StyledSaveCancelIconButton,
  StyledTruncatedText,
} from '../styled';

const EditableText = ({
  className = '',
  dataTestId = '',
  defaultText = 'None',
  errorMessage,
  hasError,
  id,
  isSaving,
  onSave,
  placeholder = '',
  value = '',
  ...other
}) => {
  const [isInEditMode, setIsInEditMode] = useState(false);
  const [internalValue, setInternalValue] = useState(value);
  const [initialValue, setInitialValue] = useState(value);
  const [internalHasError, setInternalHasError] = useState(hasError);
  const [errorValue, setErrorValue] = useState(null);
  const [areButtonsShown, setAreButtonsShown] = useState(false);

  const inputRef = useRef();
  const containerRef = useRef();

  /* handler functions */
  const handleCancel = () => {
    setInternalValue(initialValue);
    setIsInEditMode(false);
    setInternalHasError(false);
    setErrorValue(null);
  };

  const handleSave = async (newValue) => {
    onSave(newValue);
  };

  const handleSwitchToEditMode = () => {
    setIsInEditMode(true);
    waitForConditionalRender();
  };

  const waitForConditionalRender = () => {
    if (!inputRef.current) {
      window.requestAnimationFrame(waitForConditionalRender);
    } else {
      inputRef.current.focus();
    }
  };

  /* Keyboard and outside click Interactions */
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSave(internalValue);
    }
    if (event.key === 'Escape') {
      handleCancel();
    }
  };

  useOutsideClickEventListener(containerRef, () => {
    // ouside click will not fire a resave on outside click if in error state
    if (!internalHasError) {
      handleSave(internalValue);
    }
  });

  /* watcher events */

  // resets values when external value is changed
  useEffect(() => {
    setInternalValue(value);
    setInitialValue(value);
  }, [value]);

  // clears internalHasError state when typing begins
  useEffect(() => {
    if (internalHasError && internalValue !== errorValue) {
      setInternalHasError(false);
    }
  }, [internalHasError, internalValue, errorValue]);

  // saving handlers
  useEffect(() => {
    if (!isSaving && !hasError) {
      setErrorValue(null);
      setInternalHasError(false);
      setIsInEditMode(false);
      setInitialValue(value);
    }
    if (!isSaving && hasError) {
      setErrorValue(internalValue);
      setInternalHasError(true);
      handleSwitchToEditMode();
    }
  }, [isSaving, hasError, value]);

  // This is necessary because we are using isInEditMode to conditionally render the buttons, so we can't ALSO use isInEditMode to set animation opacity in css styles
  useEffect(() => {
    setAreButtonsShown(isInEditMode);
  }, [isInEditMode]);

  return isInEditMode ? (
    <StyledInputContainer className={className} data-testid={dataTestId} ref={containerRef}>
      <StyledInlineInput
        className={className ? `${className}-editable-input` : undefined}
        dataTestId={dataTestId ? `${dataTestId}-editable-input` : undefined}
        enableCustomValidation
        hasError={internalHasError}
        hideLabel
        icon={isSaving ? <StyledIndeterminateProgressCircle size="small" /> : null}
        id={id}
        isDisabled={isSaving || false}
        $isSaving={isSaving}
        label=""
        name={`Editable Text Input ${id}`}
        onChange={(e) => setInternalValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        ref={inputRef}
        size={INPUT_SIZES.SMALL}
        value={internalValue}
        {...other}
      />
      {internalHasError && <StyledErrorMessage>{errorMessage}</StyledErrorMessage>}
      <StyledButtonContainer
        $areButtonsShown={areButtonsShown}
        className={className ? `${className}-button-container` : undefined}
        dataTestId={dataTestId ? `${dataTestId}-button-container` : undefined}
        id={`${id}-button-container`}
        $isSaving={isSaving}
      >
        <StyledSaveCancelIconButton
          className={className ? `${className}-save-cancel-button` : undefined}
          dataTestId={dataTestId ? `${dataTestId}-save-button` : undefined}
          icon={<IconCheck />}
          iconSize={16}
          id={`${id}-save-button`}
          onClick={() => handleSave(internalValue)}
          text="Button"
        />
        <StyledSaveCancelIconButton
          className={className ? `${className}-save-cancel-button` : undefined}
          dataTestId={dataTestId ? `${dataTestId}-cancel-button` : undefined}
          icon={<IconClose />}
          iconSize={16}
          id={`${id}-cancel-button`}
          onClick={handleCancel}
          text="Button"
        />
      </StyledButtonContainer>
    </StyledInputContainer>
  ) : (
    <>
      <TruncationTitleLogic />
      <StyledTextContainer
        className={className ? `${className} ${className}-editable-text-container` : undefined}
        data-testid={dataTestId ? `${dataTestId}-editable-text-container` : undefined}
        id={`${id}-editable-text-container`}
        onClick={handleSwitchToEditMode}
        title="Click to edit"
      >
        <StyledTruncatedText>{value || defaultText}</StyledTruncatedText>
        <StyledEditIconWrapper>
          <StyledEditIcon
            className={className ? `${className} ${className}-pencil-icon` : undefined}
            data-testid={dataTestId ? `${dataTestId}-pencil-icon` : undefined}
            height={16}
            id={`${id}-pencil-icon`}
            width={16}
          />
        </StyledEditIconWrapper>
      </StyledTextContainer>
    </>
  );
};

EditableText.propTypes = {
  /** Adds additional class to the component for styling */
  className: PropTypes.string,
  /** id for testing */
  dataTestId: PropTypes.string,
  /** override None as default text */
  defaultText: PropTypes.string,
  /** an error message to display when value is in an error state */
  errorMessage: PropTypes.string,
  /** handles whether the value being saved is in an error state */
  hasError: PropTypes.bool.isRequired,
  /** Unique EditableText identifier  */
  id: PropTypes.string.isRequired,
  /** handles whether the save function is complete  */
  isSaving: PropTypes.bool.isRequired,
  /** callback function called upon save */
  onSave: PropTypes.func.isRequired,
  /** Add a placeholder to the input field */
  placeholder: PropTypes.string,
  /** The value of the EditableText field */
  value: PropTypes.string,
};

export { EditableText };
